import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Typography, Paper, Divider, CardMedia, Fade, IconButton, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import QRCodeStyling, { DrawType, ErrorCorrectionLevel, Options, TypeNumber, Mode, DotType, CornerDotType } from "qr-code-styling";
import ColorPicker from "../../../../components/ColorPicker";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { fetchError, showMessage } from "../../../../redux/actions";
import { getARPath, getImageURLQRIcon, resizeImage } from "../../../../utils/utils";
import { Settings } from "../../../../types/models/Settings";
import { FormikErrors } from "formik";
import UploadImageModal from "../../../../components/UploadImageModal";
import FileUploadPreview from "../../../../components/FileUploadPreview";
import { Point } from "react-easy-crop";
import { AppConstants } from "../../../../types/Constants";

interface QRCodeGenProps {
    values: Settings,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Settings>>,
    experienceMode: string
}

const QRCodeGen: React.FC<QRCodeGenProps> = ({ values, setFieldValue, experienceMode }) => {

    const dispatch = useAppDispatch();
    const businessGroupState = useAppSelector((state) => state.businessGroup);

    const [currentIconFile, setCurrentIconFile] = useState<File | undefined>(undefined);
    const [imageURL, setImageURL] = useState("");
    const [copyDisable, setCopyDisable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [openImageUpload, setOpenImageUpload] = useState<boolean>(false);
  
    const oldIconFileRef = useRef<File | undefined>();
    const oldImageIconURLRef = useRef<string>("");

    const cropperInputFileRef = useRef<File | undefined>(undefined);
    const cropperInputURLRef = useRef<string>("");

    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    const maxImageDimension = 512;

    const qrWidth = 1024;
    const qrHeight = 1024;

    const optionsRef = useRef<Options>({
      width: qrWidth,
      height: qrHeight,
      type: 'canvas' as DrawType,
      data: `https://${getARPath()}?id=` + values.identifier,
      image: '/skidattl-icon.png',
      qrOptions: {
        typeNumber: 0 as TypeNumber,
        mode: 'Byte' as Mode,
        errorCorrectionLevel: 'Q' as ErrorCorrectionLevel
      },
      imageOptions: {
        saveAsBlob: true,
        hideBackgroundDots: true,
        imageSize: 0.5,
        margin: 30,
        crossOrigin: 'anonymous',
      },
      dotsOptions: {
        color: values.qr_fgcolor,
        type: 'square' as DotType
      },
      backgroundOptions: {
        color: values.qr_bgcolor,
      },
      cornersSquareOptions: {
        color: values.qr_fgcolor,
        type: 'square' as CornerDotType,
      },
      cornersDotOptions: {
        color: values.qr_fgcolor,
          type: 'square' as CornerDotType,
        }
      });

    const qrRef = useRef<QRCodeStyling | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const qrData = useRef<Blob | null>(null);

    useEffect(() => {

      qrRef.current = new QRCodeStyling(optionsRef.current);

      if (canvasRef.current) {
        qrRef.current.append(canvasRef.current);

        fetchImage();

        qrRef.current._getElement().then(canvas => {
          if (canvas) {
            canvas.style.width = '100%';
          }
        });
      }

      return () => {
        if (canvasRef.current) {
          canvasRef.current.innerHTML = '';
          qrRef.current?._getElement().then(canvas => {
            if (canvas) {
              canvas.style.width = '100%';
            }
          }); 
        }
      };

    }, []);

    useEffect(() => {
      if (qrRef.current) {
        updateQrData();

        qrRef.current._getElement().then(canvas => {
          if (canvas) {
            canvas.style.width = '100%';
          }
        }); 

      }
    }, [optionsRef.current, qrRef.current]);

    useEffect(() => {
      if (currentIconFile) {
        setImageURL(URL.createObjectURL(currentIconFile));
      }
    }, [currentIconFile]);

    useEffect(() => {
      const newOptions = {
        ...optionsRef.current,
        image: imageURL,
        imageOptions: {
          ...optionsRef.current.imageOptions,
          margin: 30
        }
      };

      qrRef.current?.update(newOptions);
      optionsRef.current = newOptions;

      // Force a second redraw (known issue on safari)
      setTimeout(() => {
        qrRef.current?.update(optionsRef.current);
        qrRef.current?._getElement().then(canvas => {
          if (canvas) {
            canvas.style.width = '100%';
          }
        });
        updateQrData(); 
      }, 100);

    }, [imageURL]);

    useEffect(() => {
      if (!qrRef.current) return;
    
      const newDotColor = values.qr_fgcolor;
      const newBgColor = values.qr_bgcolor;
    
      const newOptions = {
        ...optionsRef.current,
        dotsOptions: {
          ...optionsRef.current.dotsOptions,
          color: newDotColor,
        },
        cornersSquareOptions: {
          ...optionsRef.current.cornersSquareOptions,
          color: newDotColor,
        },
        cornersDotOptions: {
          ...optionsRef.current.cornersDotOptions,
          color: newDotColor,
        },
        backgroundOptions: {
          ...optionsRef.current.backgroundOptions,
          color: newBgColor,
        },
      };
    
      optionsRef.current = newOptions;
      qrRef.current.update(newOptions);
      updateQrData();
    
    }, [values.qr_fgcolor, values.qr_bgcolor]);
    

    useEffect(() => {

      setCurrentIconFile(undefined);
      fetchImage();

      if (values.identifier) {
        optionsRef.current = {
          ...optionsRef.current,
          data: `https://${getARPath()}?id=` + values.identifier
        };
        qrRef.current?.update(optionsRef.current);
      }

    }, [businessGroupState.currentBusinessGroup]);

    useEffect(() => {
      if (!qrRef.current) return;
    
      const newDotColor = values.qr_fgcolor;
      const newBgColor = values.qr_bgcolor;
    
      const newOptions = {
        ...optionsRef.current,
        dotsOptions: {
          ...optionsRef.current.dotsOptions,
          color: newDotColor,
        },
        cornersSquareOptions: {
          ...optionsRef.current.cornersSquareOptions,
          color: newDotColor,
        },
        cornersDotOptions: {
          ...optionsRef.current.cornersDotOptions,
          color: newDotColor,
        },
        backgroundOptions: {
          ...optionsRef.current.backgroundOptions,
          color: newBgColor,
        },
      };
    
      optionsRef.current = newOptions;
      qrRef.current.update(newOptions);
      updateQrData();
    
    }, [values.qr_fgcolor, values.qr_bgcolor]);
    

    const fetchImage = async () => {
      try {
        const id = businessGroupState.currentBusinessGroup?.id ?? "";
        const lastModified = businessGroupState.currentBusinessGroup?.last_modified ?? "";
        let response = await fetch(getImageURLQRIcon(id, lastModified));
        const blob = await response.blob();
        if (!blob.type.startsWith("image/")) {
          setImageURL('/skidattl-icon.png');
        } else {
          const url = URL.createObjectURL(blob);
          setImageURL(url);
        }

      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };

    const onDownloadClick = () => {
      if (qrRef.current) {  
        setIsLoading(true);
        qrRef.current?.download({ extension: 'png' });
        setIsLoading(false);
      }
    };

    const updateQrData = async () => {
      if (qrRef.current) {

        setIsLoading(true);
        setCopyDisable(true);

        try {
          const rawData = await qrRef.current.getRawData("png");
          if (rawData) {
            qrData.current = (rawData as Blob);
          }
        } catch (error) {
          dispatch(fetchError("Failed to get QR Code data: " + error));
        } finally {
          setIsLoading(false);
          setCopyDisable(false);

        }
        
      }
    };

    const onCopyClick = () => {
      if (qrData.current) {
        navigator.clipboard.write([
          new ClipboardItem({ "image/png": qrData.current })
        ]);
        dispatch(showMessage("QR Code copied to clipboard"));
      }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(`https://${getARPath()}` + values.identifier).then(() => {
          dispatch(showMessage("Link copied to clipboard"));
        }).catch((error) => {
          dispatch(fetchError("Failed to copy link: " + error));
        });
      };

    const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputFile = event.target.files![0];

      if (inputFile) {
        // Check image file size
        if (inputFile.size > AppConstants.MaxImageBytes) {
          resizeImage(inputFile, maxImageDimension, maxImageDimension, AppConstants.MaxImageBytes, (resizedFile: File | undefined) => {
            if (resizedFile) {
              const imageUrl = URL.createObjectURL(resizedFile);
              setCurrentIconFile(resizedFile);
              setImageURL(imageUrl);
              setFieldValue("qrIcon", resizedFile);
              cropperInputFileRef.current = resizedFile;
              cropperInputURLRef.current = imageUrl;
            }
          });
        }
        else {
          const imageUrl = URL.createObjectURL(inputFile);
          setCurrentIconFile(inputFile);
          setImageURL(imageUrl);
          setFieldValue("qrIcon", inputFile);
          cropperInputFileRef.current = inputFile;
          cropperInputURLRef.current = imageUrl;
        }
      }

      oldIconFileRef.current = currentIconFile;
      oldImageIconURLRef.current = imageURL;
      resetCropAndZoom();
      setOpenImageUpload(true);

      oldIconFileRef.current = currentIconFile;
      oldImageIconURLRef.current = imageURL;
      resetCropAndZoom();
      setOpenImageUpload(true);

      event.target.value = "";
    };

    const handleBGColorChange = (value: string) => {
      if (value == optionsRef.current.backgroundOptions?.color) return;
        setFieldValue('qr_bgcolor', value);

        const newOptions = {
          ...optionsRef.current,
          backgroundOptions: {
                ...optionsRef.current.backgroundOptions,
                color: value,
              },
        };
        qrRef.current?.update(newOptions);
        optionsRef.current = newOptions;
    };

    const handleDotColorChange = (value: string) => {
      if (value == optionsRef.current.dotsOptions?.color) return;
        setFieldValue('qr_fgcolor', value);

        const newOptions = {
          ...optionsRef.current,
          cornersSquareOptions: {
            ...optionsRef.current.cornersSquareOptions,
            color: value,
          },
          cornersDotOptions: {
            ...optionsRef.current.cornersDotOptions,
            color: value,
          },
          dotsOptions: {
            ...optionsRef.current.dotsOptions,
            color: value,
          },
        };
        qrRef.current?.update(newOptions);
        optionsRef.current = newOptions;
    };

    const onDelete = () => {
      setCurrentIconFile(undefined);
      setFieldValue("qrIcon", null);
      setImageURL('/skidattl-icon.png');

      cropperInputFileRef.current = undefined;
      cropperInputURLRef.current = "";

    };

    const resetCropAndZoom = () => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };


    const getImageNameFromURL = (url: string) => {
      try {
        const pathname = new URL(url, window.location.href).pathname;
        return pathname.substring(pathname.lastIndexOf('/') + 1);
      } catch {
        return '';
      }
    };


    const editOriginalImage = () => {

    if (imageURL && imageURL !== cropperInputURLRef.current) {
      URL.revokeObjectURL(imageURL);
    }

      setCurrentIconFile(cropperInputFileRef.current);
      setImageURL(cropperInputURLRef.current);
      setFieldValue("qrIcon", cropperInputFileRef.current);

      setOpenImageUpload(true);
    };

    return (
      <Box
        sx={{
          padding: 3,
          backgroundColor: "white",
        }}
      >
      <Box sx={{ display: "flex", gap: 6 }}>
          <Box sx={{ display: "flex", flexDirection: 'column', alignItems: 'center'}}>
                <Paper sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                    width: "300px",
                    height: "300px",
                    display: "flex",
                    marginBottom: 2,
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative"
                  }} 
                >
                <Fade in={!isLoading} timeout={700}>
                <Box ref={canvasRef} sx={{ display: isLoading ? 'none' : 'block', width: "256px", height: "256px"}}>
                </Box>
                </Fade>
                {isLoading && 
                  <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", }}>
                    <CircularProgress />
                  </Box>
                }
                </Paper>
              <Box sx={{
                display: "flex",
                gap: 2,
                width: "300px"
               }}>
                <Button variant="contained" sx={{ paddingX: 4, marginBottom: 4, width: "100%" }} disabled={copyDisable} onClick={onCopyClick}>
                  <ContentCopyIcon sx={{ fontSize: "16px", mr: "1px"}}/>Copy
              </Button>
              <Button variant="contained" sx={{ paddingX: 4, marginBottom: 4, width: "100%" }} onClick={onDownloadClick}>
                  <DownloadIcon sx={{ fontSize: "20px", mr: "1px" }}/>Download
              </Button>
              </Box>
          </Box>
        <Paper sx={{
            backgroundColor: "#ffffff",
            borderRadius: "4px",
            marginBottom: 0,
            display: "flex",
            width: "100%"
          }}
        >
          <Box sx={{ padding: 2, width: "100%" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Experience URL</Typography>
          <Box sx={{ paddingX: 2, display: "flex", gap: 10, alignItems: "center" }}>
            <Typography>{getARPath() + values.identifier}</Typography>
            <Button variant="outlined" onClick={copyLink}>Copy</Button>
          </Box>
          <Divider sx={{ my: 4 }}/>

          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Center Icon</Typography>
            <Box sx={{ display: "flex", gap: 15, marginTop: 1, paddingX: 6, paddingY: 3, justifyContent: "start" }}>
                <Box
                  onClick={(imageURL && getImageNameFromURL(imageURL) !== "skidattl-icon.png") && cropperInputFileRef.current ? () => editOriginalImage() : undefined}
                  sx={{
                    border: 'dashed 2px silver',
                    borderRadius: 2.5,
                    p: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                    backgroundColor: '#ffffff',
                    alignContent: 'center',
                    width: 96,
                    height: 96,
                    position: "relative"
                  }}
                >
                  {imageURL ? (
                  <>
                      <CardMedia
                      sx={{ width: "100%", height: "100%", objectFit: "fill", overflow: "hidden" }}
                      component="img"
                      src={imageURL}
                      alt="beacon"
                    />
                  
                  {(imageURL && getImageNameFromURL(imageURL) !== "skidattl-icon.png") && cropperInputFileRef.current && (
                    <IconButton 
                      size="small" 
                      sx={{ 
                        position: "absolute",
                        top: 0,
                        right: 0,
                        padding: "4px",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        color: "white",
                        '&:hover': {
                          backgroundColor: "rgba(0,0,0,0.5)",
                        },
                      }}
                    >
                        <EditIcon sx={{ color: "white" }} />
                    </IconButton>
                  )}        
                  </>
                  ) : (
                    <>
                      <ImageOutlinedIcon
                      style={{
                        fontSize: 40,
                        color: 'silver',
                      }}
                    />
                  </>
                  )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "end", mb: 1 }}>
              <label htmlFor="file-input-icon-qr">
              <Button
                component="span"
                variant="outlined"
                disabled={experienceMode == "READ_ONLY"}
                sx={{
                  whiteSpace: "nowrap",
                  height: 40,
                }}>
                  Browse
              </Button>
              </label>
              <input
                id="file-input-icon-qr"
                type="file"
                accept=".png,.jpg,.jpeg,.svg"
                style={{ display: 'none' }}
                onChange={handleIconChange}
                disabled={experienceMode == "READ_ONLY"}
              />
              </Box>
            </Box>
            <Box sx={{ width: 'fit-content', display: "inline-flex" }}>
            {(currentIconFile || getImageNameFromURL(imageURL)) != "skidattl-icon.png" &&
              <Box sx={{ paddingX: 6 }}>
                <FileUploadPreview
                  file={currentIconFile}
                  imageId={businessGroupState.currentBusinessGroup?.id}
                  onDeleteUploadFile={() => onDelete()}
                />
              </Box>
            }
            </Box>  
            <Divider sx={{ my: 4 }} /> 
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Colors</Typography>
              <Box sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  gap: 20,
                  paddingX: 6,
                  paddingY: 3,
                  width: "100%",
                  }}
              >
              <Box sx={{ textAlign: "center" }}>
              <ColorPicker 
                  color={values.qr_fgcolor}
                  handleColorChange={handleDotColorChange}
                  experienceMode={experienceMode}
              />
              <Typography sx={{ fontSize: 12, mt: 1 }}>Dot Color</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
              <ColorPicker 
                color={values.qr_bgcolor}
                handleColorChange={handleBGColorChange}
                experienceMode={experienceMode}
              />
              <Typography sx={{ fontSize: 12, mt: 1}}>Background</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
      <UploadImageModal 
        open={openImageUpload}
        onConfirm={() => setOpenImageUpload(false)}
        onDeny={() => {
          setOpenImageUpload(false);
          if (imageURL && imageURL !== oldImageIconURLRef.current) {
              URL.revokeObjectURL(imageURL);
          }
          setCurrentIconFile(oldIconFileRef.current);
          setImageURL(oldImageIconURLRef.current);
          setFieldValue("qrIcon", oldIconFileRef.current);

          cropperInputFileRef.current = undefined;
          cropperInputURLRef.current = "";

          resetCropAndZoom();
        }}
        confirmButtonTitle={"Save"}
        denyButtonTitle={"Cancel"}
        setCurrentAssetFile={(croppedFile) => {
          if (croppedFile && croppedFile.size > AppConstants.MaxImageBytes) {
            // Resize the crop as it makes the image larger in size from high resolution images
            resizeImage(croppedFile, maxImageDimension, maxImageDimension, AppConstants.MaxImageBytes, (resizedFile: File | undefined) => { 
              setCurrentIconFile(resizedFile);
              setFieldValue("qrIcon", resizedFile);
            });
          } else {
            setCurrentIconFile(croppedFile);
            setFieldValue("qrIcon", croppedFile);
          }
        }}
        setCurrentImageURL={setImageURL}
        imageFile={currentIconFile}
        imageURL={imageURL}
        cropShape="rect"
        paddingValue={0.05}
        crop={crop}
        setCrop={setCrop}
        zoom={zoom}
        setZoom={setZoom}
      />
      </Box>
    );
};

export default QRCodeGen;
